import { htmlToMarkdown } from '@markbird/markdown/html-to-markdown';
import { deriveTitle } from '@markbird/shared';
import { putImage } from './images';

/**
 * Word and HTML documents, converted to Markdown on the device.
 *
 * Nothing is uploaded: `.docx` is unpacked and read by mammoth in the browser,
 * and HTML is parsed by the browser itself. Both end up in the same HTML →
 * Markdown serializer the browser extension uses, so a heading, list or table
 * comes out the same whichever way it arrived.
 *
 * Embedded images (a Word document's pictures, an HTML file's `data:` images)
 * go into the local image store and are referenced as `mbimg://`, like pasted
 * images. Left as data URIs they would put megabytes of base64 into the text.
 */

const HTML_EXT = /\.(html?|xhtml)$/i;
const DOCX_EXT = /\.docx$/i;

export function isHtmlFile(file: File): boolean {
  return HTML_EXT.test(file.name) || file.type === 'text/html';
}

export function isDocxFile(file: File): boolean {
  return (
    DOCX_EXT.test(file.name) ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

export interface ConvertedDocument {
  title: string;
  content: string;
}

/** Decode a `data:` URL into a blob, or null if it is not a base64/percent-encoded image. */
function dataUrlToBlob(url: string): Blob | null {
  const match = /^data:(image\/[\w.+-]+)?(;base64)?,(.*)$/s.exec(url);
  if (!match) return null;
  const [, type = 'image/png', base64, data = ''] = match;
  try {
    if (base64) {
      const binary = atob(data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new Blob([bytes], { type });
    }
    return new Blob([decodeURIComponent(data)], { type });
  } catch {
    return null;
  }
}

/** Move `data:` images into the image store, rewriting their `src`. */
async function storeInlineImages(root: ParentNode): Promise<void> {
  for (const img of root.querySelectorAll('img[src^="data:"]')) {
    const blob = dataUrlToBlob(img.getAttribute('src') ?? '');
    if (blob) img.setAttribute('src', await putImage(blob));
    else img.remove();
  }
}

/**
 * The part of a page worth keeping. A saved web page wraps its text in
 * navigation, headers and footers; when the page marks its content with exactly
 * one `<main>` or `<article>`, that is the document. Otherwise the whole body is,
 * because guessing wrong would silently drop text.
 */
function contentRoot(doc: Document): ParentNode {
  for (const selector of ['main', 'article']) {
    const found = doc.querySelectorAll(selector);
    if (found.length === 1 && found[0]) return found[0];
  }
  return doc.body;
}

function baseName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '') || 'Imported';
}

/** Convert an HTML document to Markdown. */
export async function convertHtml(html: string, fileName: string): Promise<ConvertedDocument> {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const root = contentRoot(doc);
  await storeInlineImages(root);
  const content = htmlToMarkdown(root);
  const pageTitle = doc.querySelector('title')?.textContent?.trim();
  return {
    title: pageTitle || deriveTitle(content, baseName(fileName)),
    content: content ? `${content}\n` : '',
  };
}

/** Convert a Word document to Markdown. */
export async function convertDocx(
  bytes: ArrayBuffer,
  fileName: string,
): Promise<ConvertedDocument> {
  // Loaded on first use: the converter and its zip reader are only needed by
  // the few people who import Word files, and weigh more than the editor.
  const mammoth = (await import('./wordReader')).default;
  const { value: html } = await mammoth.convertToHtml(
    // `arrayBuffer` is what mammoth's browser build reads; its Node build, which
    // the unit tests load, reads `buffer`. Both take the same bytes.
    { arrayBuffer: bytes, buffer: bytes } as { arrayBuffer: ArrayBuffer },
    {
      convertImage: mammoth.images.imgElement(async (image) => {
        const data = await image.readAsArrayBuffer();
        return { src: await putImage(new Blob([data], { type: image.contentType })) };
      }),
    },
  );
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const content = htmlToMarkdown(doc.body);
  return {
    // A Word file's name is usually what its author called it; a first heading
    // is used only when the name says nothing ("Document1").
    title: /^document\s*\d*$/i.test(baseName(fileName))
      ? deriveTitle(content, baseName(fileName))
      : baseName(fileName),
    content: content ? `${content}\n` : '',
  };
}
